import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { worksList, workTypeMap, type WorkType } from "@/mocks/works";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 8;

const typeOptions: { key: "all" | WorkType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "short", label: "短片" },
];

const yearOptions = [
  { key: "all", label: "全部年份" },
  { key: "2025-2026", label: "2025 - 2026" },
  { key: "2023-2024", label: "2023 - 2024" },
  { key: "2020-2022", label: "2020 - 2022" },
];

export default function WorksPage() {
  const [type, setType] = useState<"all" | WorkType>("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useSEO({
    title: "小泽玛利亚影视作品大全 - 电影电视剧短片全集",
    description:
      "小泽玛利亚影视作品大全，涵盖 2020 至 2026 年的电影、电视剧与短片，支持按类型与年份筛选，查看每部作品的简介与角色介绍。",
    keywords: "小泽玛利亚,影视作品,电影,电视剧,短片,作品列表",
  });

  const filtered = useMemo(() => {
    let list = [...worksList];
    if (type !== "all") list = list.filter((w) => w.type === type);
    if (year !== "all") {
      const [start, end] = year.split("-").map(Number);
      list = list.filter((w) => w.year >= start && w.year <= end);
    }
    list.sort((a, b) => (sort === "desc" ? b.year - a.year : a.year - b.year));
    return list;
  }, [type, year, sort]);

  useEffect(() => {
    setPage(1);
  }, [type, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const years = worksList.map((w) => w.year);

  return (
    <div className="animate-fade-up mx-auto max-w-content px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "影视作品" }]} />

      <div className="mt-5 mb-6 md:mb-8">
        <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
          Filmography
        </span>
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mt-1.5">
          影视作品
        </h1>
        <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
          收录小泽玛利亚参演的电影、电视剧与短片，年份覆盖 {Math.min(...years)} - {Math.max(...years)} 年，
          支持按类型和年份区间筛选。
        </p>
      </div>

      <button
        type="button"
        onClick={() => setFiltersOpen((v) => !v)}
        className="sm:hidden w-full flex items-center justify-between px-4 py-3 mb-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-600"></i>
          筛选与排序
        </span>
        <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
      </button>

      <div
        className={`bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 mb-6 md:mb-8 flex-col gap-4 ${
          filtersOpen ? "flex" : "hidden"
        } sm:flex`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-sm text-foreground-500 w-16 shrink-0">作品类型</span>
          <div className="flex flex-wrap gap-2">
            {typeOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setType(opt.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  type === opt.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-sm text-foreground-500 w-16 shrink-0">年份区间</span>
          <div className="flex flex-wrap gap-2">
            {yearOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setYear(opt.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  year === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-sm text-foreground-500 w-16 shrink-0">排序</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSort("desc")}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sort === "desc"
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-accent-100"
              }`}
            >
              上映时间由新到旧
            </button>
            <button
              type="button"
              onClick={() => setSort("asc")}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sort === "asc"
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-accent-100"
              }`}
            >
              上映时间由旧到新
            </button>
          </div>
        </div>
      </div>

      {paged.length === 0 ? (
        <div className="py-16 text-center">
          <i className="ri-inbox-line text-5xl text-foreground-300"></i>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mt-4">暂无相关作品</h2>
          <p className="text-sm text-foreground-500 mt-2">试试调整筛选条件，或返回首页浏览其他内容。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {paged.map((item) => (
              <WorkCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}